import {
  Container,
  DisabledButton,
  Form,
  FullLoadingIndicator,
  getTextInputProps,
  InputField,
  InputItem,
  LinkButton,
  Page,
  useToaster,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import { HelperText, Select } from '@windmill/react-ui';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  createApplication,
  relativePathTaken,
} from '../../common/api/application';
import { ToggleButton } from '../../common/components/ToggleButton';
import { AppType } from '../../common/enums';
import { DeveloperOrganizationContextProps } from '../../common/providers/types';
import {
  isReservedAppName,
  VersionIsValid,
} from '../../common/utilities/validationUtility';
import { CreateApplication } from './types';

interface RegisterAppForm {
  appUrlPath: string;
  relativePath: string;
  listingId: string;
  listingName: string;
  appType: AppType;
  shortDescription: string;
  isPublic: boolean;
  version: string;
  consoleCompatible: boolean;
}

export const ApplicationCreate: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { addToast } = useToaster();
  const { currentOrg, currentTenant } =
    useOrganization() as DeveloperOrganizationContextProps;
  const { currentTeam } = useTeam();
  const [isWebApp, setIsWebApp] = React.useState<boolean>(false);
  const [isRegistering, setIsRegistering] = React.useState<boolean>(false);
  const [listingNameValue, setListingNameValue] = React.useState<string>();
  const [typingTimeout, setTypingTimeout] = React.useState<NodeJS.Timeout>();
  const [validatingRelativePath, setRelativePathValidation] =
    React.useState<boolean>(false);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm<RegisterAppForm>({
    mode: 'onChange',
    defaultValues: {
      listingId: '',
      listingName: '',
      appUrlPath: '',
      appType: AppType.backend,
      shortDescription: '',
      isPublic: false,
      version: '',
      relativePath: '',
      consoleCompatible: false,
    },
  });
  const appType = watch('appType');
  const listingName = watch('listingName');

  useEffect(() => {
    setIsWebApp(appType === AppType.web);
  }, [appType]);

  useEffect(() => {
    setValue('appUrlPath', listingName.replace(/[^a-zA-Z]/g, ''), {
      shouldValidate: true,
    });
  }, [listingName]);

  if (!currentOrg || !currentTenant || !currentTeam) {
    return null;
  }
  const relativePathLookupTimeout = async (
    value: string
  ): Promise<boolean | string> => {
    return new Promise<boolean | string>((resolve) => {
      clearTimeout(typingTimeout);
      setTypingTimeout(
        setTimeout(() => {
          relativePathTaken(value)
            .then((result) => {
              setRelativePathValidation(false);
              if (result.available) {
                resolve(true);
              } else {
                resolve(value + ' is not available.');
              }
            })
            .catch(() => {
              setRelativePathValidation(false);
              addToast({
                message: 'Failed to see if relative path is available.',
                type: 'error',
              });
            });
        }, 400)
      );
    });
  };
  const onSubmit = handleSubmit(
    async ({
      listingName,
      appUrlPath,
      relativePath,
      isPublic,
      ...restApplication
    }: RegisterAppForm) => {
      setIsRegistering(true);
      const newApplication: CreateApplication = {
        listingName,
        isPrivate: !isPublic,
        urlPath: appUrlPath,
        url: [
          {
            name: 'relativePath',
            url: relativePath,
          },
        ],
        ...restApplication,
      };
      try {
        const { appId } = await createApplication(
          currentOrg.id,
          currentTeam.id,
          newApplication
        );

        addToast({
          message: `Successfully created application: ${listingName}`,
          type: 'success',
        });
        navigate(`/application/${appId}/credentials`);
        reset();
        setIsRegistering(false);
      } catch (e: any) {
        let message = `failed to create application: ${listingName}`;
        if (e.response.data.error?.description) {
          message = e.response.data.error.description;
        }
        addToast({
          message,
          type: 'error',
        });
        reset();
        setIsRegistering(false);
        reset();
      }
    }
  );

  if (isRegistering) {
    return (
      <Page>
        <FullLoadingIndicator
          centered={false}
          contentClassName="flex flex-col justify-center items-center font-bold text-2xl h-128 p-os1-spacing"
          fullWidth={false}
          hidden={!isRegistering}
          underHeader={true}
        >
          <div className="mt-10">
            Registering your app, this will take a few seconds
          </div>
        </FullLoadingIndicator>
      </Page>
    );
  }

  return (
    <Page>
      <Container size="medium">
        <Form
          onSubmit={onSubmit}
          // Developer guide not complete, remove hidden when ready and update url
          subtitle={
            <span className="my-5 text-gray-500" hidden={true}>
              For more info on app registration requirements&nbsp;
              <LinkButton
                linkText="See Developer Guide"
                onClick={() => window.open('https://docs.getos1.com')}
              />
            </span>
          }
          title="Register App"
        >
          <div className="flex flex-row gap-x-3">
            <InputField
              errors={errors}
              inputKey="listingName"
              placeholder="App Listing Name"
              register={register}
              registerProps={{
                required: true,
                validate: (value) => {
                  if (value === listingNameValue) {
                    return true;
                  }
                  setListingNameValue(value as string);
                  if (!value) {
                    return 'App name is required';
                  } else if (
                    value.toString().length < 2 ||
                    value.toString().length > 50
                  ) {
                    return 'App name must be 2 to 50 characters';
                  } else if (isReservedAppName(value as string)) {
                    return 'App name is reserved, please select new app name';
                  }
                  return true;
                },
              }}
              testId="listingName-input"
              title="App Listing Name"
            />
            <div>
              <InputField
                errors={errors}
                inputKey="listingId"
                placeholder="Listing ID"
                register={register}
                registerProps={{
                  required: true,
                  minLength: {
                    value: 2,
                    message: 'Must be at least 2 characters',
                  },
                  maxLength: {
                    value: 32,
                    message: 'Must be 32 or fewer characters',
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9\\._\\-]{2,32}$/,
                    message:
                      'Must contain only letters, numbers, dashes or dots',
                  },
                }}
                testId="listingId-input"
                title="Listing ID"
              />
            </div>
          </div>
          <div>
            <InputItem title="App Type">
              <Select
                css=""
                data-testid="appType-select"
                defaultValue="backend"
                {...getTextInputProps({
                  inputKey: 'appType',
                  errors,
                  register,
                  registerProps: {
                    required: true,
                    validate: (value) => {
                      if (!value) {
                        return 'App type is required';
                      }
                      return true;
                    },
                  },
                })}
              >
                <option value="web">Web</option>
                <option value="mobile">Mobile</option>
                <option value="backend">Backend</option>
              </Select>
            </InputItem>
          </div>
          <InputItem className="relative" title="Description">
            <textarea
              data-testid="shortDescription-input"
              placeholder="a short description of the app"
              {...getTextInputProps({
                inputKey: 'shortDescription',
                errors,
                register,
                className:
                  'fixed-area border dark:text-gray-300 border-gray-200 h-24 shadow w-full',
                registerProps: {
                  required: true,
                  validate: (value) => {
                    if (!value) {
                      return 'App description is required';
                    } else if (
                      (value as string).length < 16 ||
                      (value as string).length > 512
                    ) {
                      return 'Description must be between 16 and 512 characters';
                    }
                    return true;
                  },
                },
              })}
            />
            {errors['shortDescription'] && (
              <HelperText className="absolute bottom-0 left-0" valid={false}>
                {errors['shortDescription'].message}
              </HelperText>
            )}
          </InputItem>
          <div className="flex flex-row gap-x-3 relative">
            <InputItem title="Version">
              <div className="flex flex-col justify-start">
                <div>
                  <input
                    data-testid="version-input"
                    placeholder="1.0.2"
                    {...getTextInputProps({
                      inputKey: 'version',
                      errors,
                      register,
                      registerProps: {
                        required: true,
                        validate: (value) => {
                          if (!value) {
                            return 'App version is required';
                          } else if (!VersionIsValid(value as string)) {
                            return 'Incorrect format';
                          }
                          return true;
                        },
                      },
                    })}
                  />
                  {errors && errors['version'] && (
                    <HelperText
                      className="absolute bottom-4 left-0"
                      valid={false}
                    >
                      {errors['version'].message}
                    </HelperText>
                  )}
                  <div className="flex justify-end">
                    <LinkButton
                      className="mt-1"
                      linkText="Follow this format"
                      onClick={() => window.open('https://semver.org/')}
                    />
                  </div>
                </div>
              </div>
            </InputItem>
            {isWebApp && (
              <InputField
                errors={errors}
                inputKey="relativePath"
                isValidating={validatingRelativePath}
                placeholder="/app-name/relative_path/0"
                register={register}
                registerProps={{
                  required: appType === AppType.web,
                  validate: async (value) => {
                    setRelativePathValidation(true);
                    return new Promise<boolean | string>((resolve) => {
                      if (appType === AppType.web) {
                        if (!value) {
                          clearTimeout(typingTimeout);
                          setRelativePathValidation(false);
                          resolve('Relative path required for web apps.');
                        } else if (
                          !/^\/([-_/a-zA-Z0-9]+)$/.test(value as string)
                        ) {
                          clearTimeout(typingTimeout);
                          setRelativePathValidation(false);
                          const result = (value as string).replaceAll(
                            /([-_/a-zA-Z0-9]+)/g,
                            ''
                          );
                          if (value[0] !== '/') {
                            resolve('Relative path must start with /.');
                          } else if (result.length > 0) {
                            resolve(
                              'Invalid characters "' +
                                result.substring(0) +
                                '". Can be alphanumeric containing _ and -'
                            ); // ignore '/'
                          } else {
                            resolve('Incorrect format.');
                          }
                        } else {
                          relativePathLookupTimeout(value as string).then(
                            (result) => {
                              resolve(result);
                            }
                          );
                        }
                      }
                    });
                  },
                }}
                testId="relativePath-input"
                title="Relative path of the app landing page"
                validationMessage="Checking"
              />
            )}
          </div>
          <div className="flex flex-row gap-x-3">
            {/* adding div here to hide toggle button, should be removed once toggle is reinstated. */}
            <div hidden={true}>
              <ToggleButton
                className="w-90"
                control={control}
                errors={errors}
                inputKey="isPublic"
                offValue="Private"
                onValue="Public"
                register={register}
                title="Visibilty"
              >
                <span className="text-xs w-40">
                  Choosing 'Public' will publish to marketplace
                </span>
              </ToggleButton>
            </div>
            {isWebApp && (
              <InputItem title="Console Compatible" type="checkbox">
                <input
                  id="field-console-compatible"
                  type="checkbox"
                  {...getTextInputProps({
                    inputKey: 'consoleCompatible',
                    errors,
                    register,
                    className: 'border-gray-400 rounded-sm mr-3 mt-1',
                    type: 'checkbox',
                    required: false,
                  })}
                />
                <div className="flex flex-col">
                  <span className="text-xs">
                    App is compatible with the console app launch experience.
                  </span>
                  <div>
                    <LinkButton
                      className="text-sm"
                      linkText="More info"
                      onClick={() => window.open('https://docs.getos1.com')}
                    />
                  </div>
                </div>
              </InputItem>
            )}
          </div>
          <div
            className="flex flex-col mt-3"
            data-testid="register-button-container"
          >
            <DisabledButton
              className="h-10 mt-3 rounded-md w-36"
              isActive={isValid}
            >
              Register App
            </DisabledButton>
          </div>
        </Form>
      </Container>
    </Page>
  );
};
